/**
 * Local-date helpers for the tasks module.
 * These avoid UTC timezone shifting by working in the user's local timezone.
 */

// Helper to format local date as YYYY-MM-DD (avoids UTC timezone issues)
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper to parse YYYY-MM-DD as local date (not UTC)
export function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Helper to convert YYYY-MM-DD to ISO string (preserves date across timezones)
export function localDateToISO(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  // Create date at noon local time to avoid timezone shifting
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toISOString();
}

export function getDateInDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Combine a local YYYY-MM-DD with an optional HH:MM time into an ISO string.
 * With no time, falls back to the noon-local sentinel (the all-day convention
 * shared with the calendar's taskToCalendarEvent mapper). NOTE: a 12:00 time is
 * indistinguishable from all-day under that sentinel.
 */
export function localDateTimeToISO(dateStr: string, timeStr?: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (timeStr) {
    const [h, m] = timeStr.split(':').map(Number);
    const d = new Date(year, month - 1, day, h, m, 0, 0);
    // 12:00:00.000 local IS the all-day sentinel — nudge an explicit noon pick by
    // 1ms so a real 12:00 PM due time isn't read back as all-day (the ms is
    // dropped on display; the calendar marker is imperceptibly off noon).
    if (h === 12 && m === 0) d.setMilliseconds(1);
    return d.toISOString();
  }
  return new Date(year, month - 1, day, 12, 0, 0).toISOString();
}

/** Extract the LOCAL YYYY-MM-DD from a stored dueAt (ISO or already date-only). */
export function dueAtToLocalDate(dueAt?: string | null): string {
  if (!dueAt) return '';
  if (DATE_ONLY_RE.test(dueAt)) return dueAt;
  const d = new Date(dueAt);
  return Number.isNaN(d.getTime()) ? '' : toLocalDateString(d);
}

/**
 * Extract the LOCAL HH:MM from a stored dueAt, or '' when it's all-day — i.e. a
 * date-only string or the noon-local sentinel (12:00:00.000 local).
 */
export function dueAtToLocalTime(dueAt?: string | null): string {
  if (!dueAt || DATE_ONLY_RE.test(dueAt)) return '';
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return '';
  if (d.getHours() === 12 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
